import { 
  CognitiveMetrics, 
  MovementPattern, 
  SpatialNavigationMetric, 
  DualTaskPerformance,
  CognitiveWarning,
  CognitiveHealthSummary,
  RiskLevel,
  TrendDirection,
  CognitiveMetricType
} from '../interfaces/types';

// Helper to generate a random number within a range
const randomInRange = (min: number, max: number): number => {
  return Math.round((Math.random() * (max - min) + min) * 100) / 100;
};

// Helper to randomly choose an item from an array
const randomChoice = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

// Helper to generate a date in the recent past
const generatePastDate = (daysAgo: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date;
};

// Generate date string for display (e.g., "Mon", "Tue")
const getDayLabel = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

// Generate a collection of cognitive metrics for a week
export const generateWeeklyCognitiveMetrics = (): CognitiveMetrics[] => {
  const metrics: CognitiveMetrics[] = [];
  
  // Base values that will be modified slightly each day
  let baseRoutineAdherence = randomInRange(85, 95);
  let baseSocialInteractions = randomInRange(6, 10);
  let baseActivityVariation = randomInRange(80, 90);
  let baseNavigationScore = randomInRange(85, 95);
  let baseMemoryScore = randomInRange(85, 95);
  let baseConfusionEvents = randomInRange(0, 3);
  let baseExecutiveFunction = randomInRange(80, 95);
  let baseProcessingSpeed = randomInRange(75, 90);
  let baseAttentionScore = randomInRange(80, 95);
  let baseDualtaskPerformance = randomInRange(75, 90);
  
  // Generate a subtle declining trend
  const subtleDeclineFactor = 0.98; // 2% decline
  
  // Generate data for each day of the week
  for (let i = 6; i >= 0; i--) {
    // Apply subtle decline to cognitive functions
    if (i < 6) {
      baseNavigationScore *= subtleDeclineFactor;
      baseMemoryScore *= subtleDeclineFactor;
      baseExecutiveFunction *= subtleDeclineFactor;
      baseAttentionScore *= subtleDeclineFactor;
    }
    
    // Add random daily variation
    metrics.push({
      date: getDayLabel(i),
      timestamp: generatePastDate(i),
      routineAdherence: Math.min(100, baseRoutineAdherence * randomInRange(0.98, 1.02)),
      socialInteractions: Math.round(baseSocialInteractions * randomInRange(0.8, 1.2)),
      activityVariation: Math.min(100, baseActivityVariation * randomInRange(0.95, 1.05)),
      navigationScore: Math.min(100, baseNavigationScore * randomInRange(0.97, 1.03)),
      memoryScore: Math.min(100, baseMemoryScore * randomInRange(0.97, 1.03)),
      confusionEvents: Math.max(0, Math.round(baseConfusionEvents * randomInRange(0.5, 1.5))),
      executiveFunction: Math.min(100, baseExecutiveFunction * randomInRange(0.97, 1.03)),
      processingSpeed: Math.min(100, baseProcessingSpeed * randomInRange(0.97, 1.03)),
      attentionScore: Math.min(100, baseAttentionScore * randomInRange(0.97, 1.03)),
      dualtaskPerformance: Math.min(100, baseDualtaskPerformance * randomInRange(0.97, 1.03))
    });
  }
  
  return metrics;
};

// Generate movement patterns
export const generateMovementPatterns = (count: number): MovementPattern[] => {
  const patterns: MovementPattern[] = [];
  const locations = ['Bedroom', 'Bathroom', 'Kitchen', 'Living Room', 'Hallway', 'Dining Room'];
  const patternTypes: Array<'ROUTINE' | 'ANOMALOUS' | 'NEW'> = ['ROUTINE', 'ROUTINE', 'ROUTINE', 'ANOMALOUS', 'NEW'];
  
  for (let i = 0; i < count; i++) {
    const patternType = randomChoice(patternTypes);
    const locationCount = randomInRange(2, 5);
    const locationPath: string[] = [];
    
    // Generate a path through the house
    for (let j = 0; j < locationCount; j++) {
      let nextLocation = randomChoice(locations);
      // Avoid repeating the same location consecutively
      while (j > 0 && nextLocation === locationPath[j-1]) {
        nextLocation = randomChoice(locations);
      }
      locationPath.push(nextLocation);
    }
    
    // Confidence is higher for routine patterns
    const confidence = patternType === 'ROUTINE' ? randomInRange(80, 100) : 
                       patternType === 'ANOMALOUS' ? randomInRange(70, 90) : 
                       randomInRange(60, 80);
    
    // Deviation is lower for routine patterns                   
    const deviation = patternType === 'ROUTINE' ? randomInRange(0, 20) : 
                     patternType === 'ANOMALOUS' ? randomInRange(30, 70) : 
                     randomInRange(20, 40);
    
    patterns.push({
      id: `pattern-${i}`,
      timestamp: new Date(Date.now() - randomInRange(0, 24 * 60 * 60 * 1000)),
      patternType,
      locationPath,
      duration: randomInRange(30, 300), // 30 seconds to 5 minutes
      averageSpeed: randomInRange(0.5, 2.0),
      pauseFrequency: patternType === 'ROUTINE' ? randomInRange(0, 3) : randomInRange(2, 8),
      turningAngles: Array(Math.round(randomInRange(1, 5))).fill(0).map(() => randomInRange(0, 180)),
      confidence,
      deviationFromBaseline: deviation
    });
  }
  
  return patterns;
};

// Generate spatial navigation metrics
export const generateSpatialNavigationMetrics = (count: number): SpatialNavigationMetric[] => {
  const metrics: SpatialNavigationMetric[] = [];
  const locations = ['Bedroom', 'Bathroom', 'Kitchen', 'Living Room', 'Hallway', 'Dining Room'];
  
  for (let i = 0; i < count; i++) {
    // Pick different start and end locations
    let startLocation = randomChoice(locations);
    let endLocation = randomChoice(locations.filter(loc => loc !== startLocation));
    
    // Higher familiarity for common paths
    const routineFamiliarity = Math.random() < 0.7 ? 
      randomInRange(75, 100) : randomInRange(40, 75);
    
    // More efficient paths for familiar routes
    const pathEfficiency = routineFamiliarity > 75 ? 
      randomInRange(80, 100) : randomInRange(50, 80);
    
    // Fewer errors on familiar paths
    const wrongTurns = routineFamiliarity > 75 ? 
      Math.round(randomInRange(0, 1)) : Math.round(randomInRange(1, 3));
      
    metrics.push({
      id: `nav-${i}`,
      timestamp: new Date(Date.now() - randomInRange(0, 24 * 60 * 60 * 1000)),
      startLocation,
      endLocation,
      pathEfficiency,
      hesitations: Math.round(randomInRange(0, 5)),
      wrongTurns,
      timeToComplete: randomInRange(15, 120), // 15 seconds to 2 minutes
      distanceCovered: randomInRange(5, 30), // 5 to 30 meters
      routineFamiliarity
    });
  }
  
  return metrics;
};

// Generate dual task performance metrics
export const generateDualTaskPerformances = (count: number): DualTaskPerformance[] => {
  const metrics: DualTaskPerformance[] = [];
  
  const primaryTasks = [
    'Walking to kitchen', 
    'Preparing meal', 
    'Getting dressed', 
    'Organizing medications',
    'Making bed'
  ];
  
  const secondaryTasks = [
    'Carrying conversation',
    'Recalling shopping list',
    'Monitoring timer',
    'Responding to questions',
    'Following instructions'
  ];
  
  for (let i = 0; i < count; i++) {
    const primaryTaskPerformance = randomInRange(70, 95);
    const secondaryTaskPerformance = randomInRange(65, 90);
    
    // Overall performance is weighted toward the primary task
    const overallPerformance = primaryTaskPerformance * 0.6 + secondaryTaskPerformance * 0.4;
    
    metrics.push({
      id: `dualtask-${i}`,
      timestamp: new Date(Date.now() - randomInRange(0, 24 * 60 * 60 * 1000)),
      primaryTask: randomChoice(primaryTasks),
      secondaryTask: randomChoice(secondaryTasks),
      primaryTaskPerformance,
      secondaryTaskPerformance,
      overallPerformance,
      attentionSwitches: Math.round(randomInRange(3, 12)),
      completionTime: randomInRange(30, 240), // 30 seconds to 4 minutes
      errorRate: randomInRange(5, 30)
    });
  }
  
  return metrics;
};

// Generate cognitive warnings
export const generateCognitiveWarnings = (count: number): CognitiveWarning[] => {
  const warnings: CognitiveWarning[] = [];
  
  const metricTypes: CognitiveMetricType[] = [
    'MEMORY',
    'SPATIAL_NAVIGATION',
    'TASK_SWITCHING',
    'ATTENTION',
    'EXECUTIVE_FUNCTION',
    'PROCESSING_SPEED'
  ];
  
  const warningTypes: Array<'ACUTE_CHANGE' | 'GRADUAL_DECLINE' | 'PATTERN_BREAK' | 'THRESHOLD_BREACH'> = [
    'ACUTE_CHANGE',
    'GRADUAL_DECLINE',
    'PATTERN_BREAK',
    'THRESHOLD_BREACH'
  ];
  
  const severityLevels: RiskLevel[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  
  const directions: TrendDirection[] = ['IMPROVING', 'STABLE', 'DECLINING', 'FLUCTUATING'];
  
  const warningDescriptions: Record<string, string[]> = {
    'MEMORY': [
      'Decreased recall of recent events',
      'Difficulty remembering medication schedule',
      'Increased repetition in conversations',
      'Misplacing personal items more frequently'
    ],
    'SPATIAL_NAVIGATION': [
      'Increased hesitation when navigating familiar routes',
      'More wrong turns in home environment',
      'Taking longer paths to common destinations',
      'Confusion about room locations'
    ],
    'TASK_SWITCHING': [
      'Difficulty transitioning between activities',
      'Incomplete task sequences',
      'Extended time spent shifting focus',
      'Increased errors when multitasking'
    ],
    'ATTENTION': [
      'Shorter attention span during routine activities',
      'More easily distracted by environmental stimuli',
      'Difficulty maintaining focus on conversations',
      'Increased time to complete attention-demanding tasks'
    ],
    'EXECUTIVE_FUNCTION': [
      'Decreased planning efficiency',
      'More impulsive decision-making',
      'Difficulty organizing daily activities',
      'Problems adapting to unexpected changes'
    ],
    'PROCESSING_SPEED': [
      'Slower response to questions',
      'Delayed reactions to environmental changes',
      'Extended processing time for instructions',
      'Increased latency in decision-making'
    ]
  };
  
  for (let i = 0; i < count; i++) {
    const metricType = randomChoice(metricTypes);
    const warningType = randomChoice(warningTypes);
    const severity = randomChoice(severityLevels);
    const trend: TrendDirection = warningType === 'GRADUAL_DECLINE' ? 'DECLINING' : randomChoice(directions);
    
    warnings.push({
      id: `warning-${i}`,
      timestamp: new Date(Date.now() - randomInRange(0, 7 * 24 * 60 * 60 * 1000)), // Up to a week ago
      metricType,
      warningType,
      severity,
      description: randomChoice(warningDescriptions[metricType]),
      relatedMetrics: ['routineAdherence', 'navigationScore', 'memoryScore', 'confusionEvents'].slice(0, Math.round(randomInRange(1, 4))),
      confidence: randomInRange(60, 95),
      recommendedActions: [
        'Consult healthcare provider for cognitive assessment',
        'Review medication regimen for potential side effects',
        'Increase environmental cues and reminders',
        'Simplify daily routine structure'
      ].slice(0, Math.round(randomInRange(1, 4))),
      trend: {
        direction: trend,
        duration: Math.round(randomInRange(1, 30)), // 1-30 days
        magnitude: randomInRange(5, 30) // 5-30% change
      },
      isAcknowledged: Math.random() > 0.7 // 30% acknowledged
    });
  }
  
  // Sort by severity (critical first)
  return warnings.sort((a, b) => {
    const severityOrder = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
};

// Generate cognitive health summary
export const generateCognitiveHealthSummary = (): CognitiveHealthSummary => {
  // Generate base score with subtle decline
  const baseScore = randomInRange(80, 90);
  
  // Determine risk level based on score
  let riskLevel: RiskLevel;
  if (baseScore >= 85) riskLevel = 'LOW';
  else if (baseScore >= 75) riskLevel = 'MEDIUM';
  else if (baseScore >= 65) riskLevel = 'HIGH';
  else riskLevel = 'CRITICAL';
  
  // Generate trend directions for different metrics
  const trends: {[key: string]: {direction: TrendDirection, value: string}} = {
    'cognitive': { 
      direction: 'DECLINING', 
      value: '-2%' 
    },
    'routine': { 
      direction: 'STABLE', 
      value: 'stable' 
    },
    'navigation': { 
      direction: 'DECLINING', 
      value: '-3%' 
    },
    'memory': { 
      direction: 'DECLINING', 
      value: '-2%' 
    },
    'social': { 
      direction: 'IMPROVING', 
      value: '+5%' 
    },
    'executive': { 
      direction: 'STABLE', 
      value: 'stable' 
    }
  };
  
  return {
    overallScore: baseScore,
    riskLevel,
    keyStrengths: [
      'Consistent morning routine adherence',
      'Strong social engagement patterns',
      'Stable sleep-wake rhythms',
      'Good meal preparation sequences'
    ],
    keyWeaknesses: [
      'Slight decline in spatial navigation efficiency',
      'Minor short-term memory fluctuations',
      'Occasional difficulty with dual-task activities',
      'Evening medication adherence variability'
    ],
    significantChanges: [
      {
        metric: 'Navigation Efficiency',
        changePercent: -3,
        timeframe: 'Past 2 weeks'
      },
      {
        metric: 'Dual Task Performance',
        changePercent: -2,
        timeframe: 'Past month'
      },
      {
        metric: 'Social Engagement',
        changePercent: 5,
        timeframe: 'Past week'
      }
    ],
    warningCount: Math.round(randomInRange(1, 4)),
    trends,
    lastUpdated: new Date()
  };
}; 